import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DocumentsView() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDocuments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/documents`, { withCredentials: true });
      setDocuments(data);
    } catch (error) {
      toast.error('Error al cargar los documentos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
    const handler = () => loadDocuments();
    window.addEventListener('vida:data-changed', handler);
    return () => window.removeEventListener('vida:data-changed', handler);
  }, [loadDocuments]);

  const deleteDocument = async (docId) => {
    if (!window.confirm('¿Eliminar este documento? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/api/documents/${docId}`, { withCredentials: true });
      toast.success('Documento eliminado');
      loadDocuments();
    } catch {
      toast.error('No pudimos eliminar el documento');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-12 h-12 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="documents-view">
      <div>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-2" >
          Documentos
        </h1>
        <p className="text-muted-foreground">Tus facturas, recibos y archivos importantes</p>
      </div>

      <div className="bg-surface rounded-2xl shadow-md p-5 sm:p-8">
        {documents.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-muted-foreground text-lg">No hay documentos guardados</p>
            <p className="text-muted-foreground text-sm mt-2">Sube un documento usando el botón + Agregar</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map((doc) => (
              <div
                key={doc.id}
                data-testid={`document-${doc.id}`}
                className="p-5 sm:p-6 bg-surface-2 rounded-2xl hover:shadow-md transition-all border border-border flex flex-col"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-foreground" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground truncate">
                      {doc.service_name || doc.original_filename}
                    </h3>
                    {doc.document_type && (
                      <p className="text-xs text-primary font-semibold uppercase tracking-wider mt-0.5">
                        {String(doc.document_type).replace('_', ' ')}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1 truncate">{doc.original_filename}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(doc.created_at).toLocaleDateString('es-AR')} · {(doc.size / 1024).toFixed(1)} KB
                    </p>
                    {(doc.amount || doc.due_date) && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {doc.amount ? `${doc.currency || ''} ${doc.amount}`.trim() : ''}
                        {doc.amount && doc.due_date ? ' · ' : ''}
                        {doc.due_date || ''}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <a
                    href={`${API}/api/documents/${doc.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`download-${doc.id}`}
                    className="flex-1 flex items-center justify-center gap-2 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#152B47] transition-all text-sm font-semibold"
                  >
                    <Download className="w-4 h-4" strokeWidth={2} />
                    Descargar
                  </a>
                  <button
                    data-testid={`delete-document-${doc.id}`}
                    onClick={() => deleteDocument(doc.id)}
                    title="Eliminar documento"
                    className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-lg transition-all border border-border"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
